import { useState, useEffect, useContext } from 'react';
import { Row, Col, Spinner, Alert } from 'react-bootstrap';
import PeriodFilter from '../PeriodFilter';
import StatCard from '../StatCard';
import StatCardCompact from '../StatCardCompact';
import CustomPeriodModal from '../CustomPeriodModal';
import EvolucionTemporalChart from '../EvolucionTemporalChart';
import AtencionesPorMotivoChart from '../AtencionesPorMotivoChart';
import DuracionPromedioPorTipoChart from '../DuracionPromedioPorTipoChart';
import UltimasAtencionesTable from '../UltimasAtencionesTable';
import { PeriodContext } from '../../../pages/jefatura/Dashboard';
import { obtenerEstadisticasTickets, obtenerEvolucionTemporal, obtenerAtencionesPorMotivo, obtenerDuracionPromedioPorTipo, obtenerUltimasAtenciones } from '../../../services/apis_jefatura';

function TicketsTab() {
  // Usar el contexto del Dashboard para el período
  const { selectedPeriod, setSelectedPeriod, customDates, setCustomDates } = useContext(PeriodContext);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [motivosData, setMotivosData] = useState(null);
  const [loadingMotivos, setLoadingMotivos] = useState(false);
  const [duracionData, setDuracionData] = useState(null);
  const [loadingDuracion, setLoadingDuracion] = useState(false);
  const [atencionesData, setAtencionesData] = useState(null);
  const [loadingAtenciones, setLoadingAtenciones] = useState(false);

  // Estado para las estadísticas (inicializado con valores en cero)
  const [stats, setStats] = useState({
    totalAtenciones: {
      value: "0",
      subtitle: "Periodo seleccionado",
    },
    tiempoPromedio: {
      value: "0 min",
      subtitle: "Min: 0 min - Max: 0 min",
    },
    ticketsAtendidos: {
      value: "0",
      subtitle: "0%",
    },
    horarioPeak: {
      value: "N/A",
      subtitle: "0 tickets promedio",
    },
    motivoPrincipal: {
      value: "N/A",
      subtitle: "0 atenciones (0%)",
    },
    diaMayorDemanda: {
      value: "N/A",
      subtitle: "0 atenciones promedio",
    },
  });

  // Función para cargar datos de la API
  const cargarEstadisticas = async (params) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await obtenerEstadisticasTickets(params);
      
      // Transformar los datos de la API al formato que necesita el componente
      setStats({
        totalAtenciones: {
          value: data.total_atenciones?.toString() || "0",
          subtitle: "Periodo seleccionado",
        },
        tiempoPromedio: {
          value: `${data.tiempo_promedio || 0} min`,
          subtitle: `Min: ${data.tiempo_min || 0} min - Max: ${data.tiempo_max || 0} min`,
        },
        ticketsAtendidos: {
          value: data.tickets_atendidos?.toString() || "0",
          subtitle: `${data.porcentaje_atendidos || 0}%`,
        },
        horarioPeak: {
          value: data.horario_peak || "N/A",
          subtitle: `${data.tickets_promedio_peak || 0} tickets promedio`,
        },
        motivoPrincipal: {
          value: data.motivo_principal || "N/A",
          subtitle: `${data.atenciones_motivo_principal || 0} atenciones (${data.porcentaje_motivo || 0}%)`,
        },
        diaMayorDemanda: {
          value: data.dia_mayor_demanda || "N/A",
          subtitle: `${data.atenciones_promedio_dia || 0} atenciones promedio`,
        },
      });
    } catch (err) {
      console.error("Error cargando estadísticas:", err);
      setError("No se pudieron cargar las estadísticas. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar datos del gráfico
  const cargarGrafico = async (params) => {
    setLoadingChart(true);
    
    try {
      const data = await obtenerEvolucionTemporal(params);
      setChartData(data);
    } catch (err) {
      console.error("Error cargando gráfico:", err);
      setChartData(null);
    } finally {
      setLoadingChart(false);
    }
  };

  // Función para cargar datos de motivos
  const cargarMotivos = async (params) => {
    setLoadingMotivos(true);
    
    try {
      const data = await obtenerAtencionesPorMotivo(params);
      setMotivosData(data);
    } catch (err) {
      console.error("Error cargando motivos:", err);
      setMotivosData(null);
    } finally {
      setLoadingMotivos(false);
    }
  };

  // Función para cargar datos de duración promedio
  const cargarDuracion = async (params) => {
    setLoadingDuracion(true);
    
    try {
      const data = await obtenerDuracionPromedioPorTipo(params);
      setDuracionData(data);
    } catch (err) {
      console.error("Error cargando duración:", err);
      setDuracionData(null);
    } finally {
      setLoadingDuracion(false);
    }
  };

  // Función para cargar últimas atenciones
  const cargarAtenciones = async (params) => {
    setLoadingAtenciones(true);
    
    try {
      const data = await obtenerUltimasAtenciones(params.period, params.startDate, params.endDate, 50);
      setAtencionesData(data.atenciones);
    } catch (err) {
      console.error("Error cargando atenciones:", err);
      setAtencionesData(null);
    } finally {
      setLoadingAtenciones(false);
    }
  };

  // useEffect para cargar datos cuando cambia el período
  useEffect(() => {
    let params = {};
    
    if (selectedPeriod === "custom" && customDates) {
      params = {
        startDate: customDates.startDate,
        endDate: customDates.endDate,
      };
    } else if (selectedPeriod !== "custom") {
      params = { period: selectedPeriod };
    }

    cargarEstadisticas(params);
    cargarGrafico(params);
    cargarMotivos(params);
    cargarDuracion(params);
    cargarAtenciones(params);
  }, [selectedPeriod, customDates]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period === "custom") {
      setShowCustomModal(true);
    }
  };

  const handleCustomPeriodApply = (startDate, endDate) => {
    setCustomDates({ startDate, endDate });
    setShowCustomModal(false);
  };

  return (
    <div>
      {/* Filtro de período */}
      <Row className="mb-4">
        <Col>
          <PeriodFilter
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
          />
        </Col>
      </Row>

      {/* Loading spinner */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
          <p className="mt-3 text-muted">Cargando estadísticas...</p>
        </div>
      )}

      {/* Error alert */}
      {error && !loading && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Cards de estadísticas */}
      {!loading && !error && (
        <Row className="g-4">
          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Total de atenciones"
              value={stats.totalAtenciones.value}
              subtitle={stats.totalAtenciones.subtitle}
              icon="bi bi-ticket-perforated"
              iconColor="blue"
              valueColor="blue"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Tiempo Promedio"
              value={stats.tiempoPromedio.value}
              subtitle={stats.tiempoPromedio.subtitle}
              icon="bi bi-clock"
              iconColor="cyan"
              valueColor="cyan"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCard
              title="Tickets Atendidos"
              value={stats.ticketsAtendidos.value}
              subtitle={stats.ticketsAtendidos.subtitle}
              icon="bi bi-check-circle"
              iconColor="green"
              valueColor="green"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCardCompact
              title="Horario Peak"
              value={stats.horarioPeak.value}
              subtitle={stats.horarioPeak.subtitle}
              icon="bi bi-clock-history"
              iconColor="blue"
              valueColor="blue"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCardCompact
              title="Motivo Principal"
              value={stats.motivoPrincipal.value}
              subtitle={stats.motivoPrincipal.subtitle}
              icon="bi bi-card-list"
              iconColor="cyan"
              valueColor="cyan"
            />
          </Col>

          <Col xs={12} md={6} lg={4}>
            <StatCardCompact
              title="Día con Mayor Demanda"
              value={stats.diaMayorDemanda.value}
              subtitle={stats.diaMayorDemanda.subtitle}
              icon="bi bi-calendar-check"
              iconColor="orange"
              valueColor="orange"
            />
          </Col>
        </Row>
      )}

      {/* Gráfico de evolución temporal */}
      {!loading && (
        <Row className="mt-4">
          <Col xs={12}>
            {loadingChart ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando gráfico...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando gráfico...</p>
                </div>
              </div>
            ) : (
              <EvolucionTemporalChart
                data={chartData}
                period={selectedPeriod}
                customDates={customDates}
              />
            )}
          </Col>
        </Row>
      )}

      {/* Gráficos de barras: Atenciones por motivo y Duración promedio */}
      {!loading && (
        <Row className="g-4 mb-4">
          <Col xs={12} lg={6}>
            {loadingMotivos ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando motivos...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando motivos...</p>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm">
                <div className="card-body">
                  <AtencionesPorMotivoChart
                    data={motivosData}
                    period={selectedPeriod}
                  />
                </div>
              </div>
            )}
          </Col>
          
          <Col xs={12} lg={6}>
            {loadingDuracion ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando duración...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando duración...</p>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm">
                <div className="card-body">
                  <DuracionPromedioPorTipoChart
                    data={duracionData}
                    period={selectedPeriod}
                  />
                </div>
              </div>
            )}
          </Col>
        </Row>
      )}

      {/* Tabla de últimas atenciones */}
      {!loading && (
        <Row className="g-4">
          <Col xs={12}>
            {loadingAtenciones ? (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Cargando atenciones...</span>
                  </Spinner>
                  <p className="mt-3 text-muted">Cargando últimas atenciones...</p>
                </div>
              </div>
            ) : (
              <UltimasAtencionesTable
                atenciones={atencionesData}
                title="Últimas Atenciones"
              />
            )}
          </Col>
        </Row>
      )}

      {/* Modal para período personalizado */}
      <CustomPeriodModal
        show={showCustomModal}
        onHide={() => setShowCustomModal(false)}
        onApply={handleCustomPeriodApply}
      />
    </div>
  );
}

export default TicketsTab;
